import { useState, useEffect } from 'react'
import { useTheme } from '../contexts/ThemeContext'

const SessionTimeoutModal = ({ isOpen, onStayLoggedIn, onLogout, secondsRemaining }) => {
  const { isNight } = useTheme()
  const [countdown, setCountdown] = useState(secondsRemaining)

  useEffect(() => {
    setCountdown(secondsRemaining)
  }, [secondsRemaining])

  useEffect(() => {
    if (!isOpen || countdown <= 0) return

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          onLogout()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [isOpen, onLogout, countdown])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className={`fixed inset-0 transition-opacity ${
          isNight ? 'bg-black/80' : 'bg-gray-500/75'
        }`}
        onClick={onStayLoggedIn}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={`relative transform overflow-hidden rounded-lg text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg ${
            isNight
              ? 'bg-void-light border border-void-elevated'
              : 'bg-white'
          }`}
        >
          <div className="px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              {/* Warning Icon */}
              <div
                className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                  isNight ? 'bg-amber-900/30' : 'bg-amber-100'
                }`}
              >
                <svg
                  className={`h-6 w-6 ${isNight ? 'text-amber-400' : 'text-amber-600'}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>

              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <h3
                  className={`text-lg font-semibold leading-6 ${
                    isNight ? 'text-white font-orbitron' : 'text-gray-900'
                  }`}
                >
                  {isNight ? 'SESSION EXPIRING' : 'Session Expiring'}
                </h3>
                <div className="mt-2">
                  <p className={`text-sm ${isNight ? 'text-gray-300' : 'text-gray-500'}`}>
                    Your session will expire in{' '}
                    <span className={`font-bold ${isNight ? 'text-cyber-cyan' : 'text-primary-600'}`}>
                      {countdown} seconds
                    </span>
                    . Would you like to stay logged in?
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 ${
              isNight ? 'bg-void' : 'bg-gray-50'
            }`}
          >
            <button
              type="button"
              onClick={onStayLoggedIn}
              className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm sm:ml-3 sm:w-auto ${
                isNight
                  ? 'bg-cyber-cyan text-void hover:bg-cyber-cyan/90'
                  : 'bg-primary-600 text-white hover:bg-primary-500'
              }`}
            >
              {isNight ? 'STAY CONNECTED' : 'Stay Logged In'}
            </button>
            <button
              type="button"
              onClick={onLogout}
              className={`mt-3 inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm ring-1 ring-inset sm:mt-0 sm:w-auto ${
                isNight
                  ? 'bg-void-elevated text-gray-300 ring-void-elevated hover:bg-void hover:text-white'
                  : 'bg-white text-gray-900 ring-gray-300 hover:bg-gray-50'
              }`}
            >
              {isNight ? 'DISCONNECT' : 'Log Out'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SessionTimeoutModal
